import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EventService } from '@app/events/shared/event.service';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { TimePickerComponent } from '@app/partials/form/time-picker/time-picker.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { ProductItemFormComponent } from '@app/events/view/product-item-form.component';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { Edit, LucideAngularModule, Trash } from 'lucide-angular';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { CurrencyPipe } from '@angular/common';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { DateUtil } from '@app/shared/utils/date.util';
import { RouterLink } from '@angular/router';
import { FeatureFlags } from '@app/config/feature-flags';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { EventProductItemService } from '@app/events/shared/event-product-item.service';
import { RouteParams } from '@app/shared/abstract/route-params';

@Component({
  selector: 'app-event-timeline-item-add',
  templateUrl: './timeline-item-add.component.html',
  styleUrls: [],
  providers: [provideFlatpickrDefaults()],
  imports: [
    ButtonComponent,
    CheckboxComponent,
    DatePickerComponent,
    TextInputComponent,
    TimePickerComponent,
    ProductItemFormComponent,
    LucideAngularModule,
    CurrencyPipe,
    TextareaComponent,
    RouterLink,
    SelectComponent,
  ],
  standalone: true,
})
export class TimelineItemAddComponent implements OnInit {
  @Input() public event!: EventModel;
  @Output() toggleOpen = new EventEmitter<any>();
  @Output() itemCreated = new EventEmitter<EventTimelineItemModel>();

  protected processing = false;
  protected productItems: EventProductItemModel[] = [];
  protected linkedProductItemIdsControl = new FormControl<number[]>([], { nonNullable: true });
  protected existingProductChoices: { value: number; label: string }[] = [];

  protected readonly EditIcon = Edit;
  protected readonly TrashIcon = Trash;
  protected readonly showProductManagement = FeatureFlags.ITINERARY_PRODUCT_MANAGEMENT;

  protected form: FormGroup<{
    date: FormControl<string | null>;
    time: FormControl<string | null>;
    is_published: FormControl<false | true | null>;
    name: FormControl<string | null>;
    description: FormControl<string | null>;
  }>;

  constructor(
    private eventService: EventService,
    private eventProductItemService: EventProductItemService,
  ) {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      is_published: new FormControl(false),
      date: new FormControl('', [Validators.required]),
      time: new FormControl('', [Validators.required]),
      description: new FormControl(''),
    });
  }

  ngOnInit(): void {
    if (this.event.start_date) {
      let defaultDateTime = this.event.start_date;
      
      // If arrival is available, use that as the default time
      if (this.event.arrival) {
        defaultDateTime = this.event.arrival;
      }
      
      // Split the datetime for the form
      const split = DateUtil.splitDateTime(defaultDateTime);
      this.form.patchValue({
        date: split.date || '',
        time: split.time || '',
      });
    }

    this.loadExistingProducts();
    this.trackLinkedProductSelectionChanges();
  }


  save() {
    if (!this.form) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    const data = this.form.value as any;
    data.event_id = this.event.id;

    // Clean product items: remove nested objects and negative IDs
    data.product_items = this.productItems.map((item) => {
      const cleanedItem: any = {
        quantity: item.quantity,
        amount: item.amount,
        date_purchased: item.date_purchased,
        is_paid: item.is_paid,
        product_id: item.product_id,
        event_id: item.event_id,
      };

      // Only include ID for existing products (positive IDs), omit for new products (negative IDs)
      if (item.id && item.id > 0) {
        cleanedItem.id = item.id;
      }

      // Clean variant selections: keep only essential fields, remove nested objects
      if (item.variant_selections && item.variant_selections.length > 0) {
        cleanedItem.variant_selections = item.variant_selections.map((selection: any) => ({
          variant_id: selection.variant_id,
          variant_option_id: selection.variant_option_id,
          quantity: selection.quantity,
          notes: selection.notes,
        }));
      }

      return cleanedItem;
    });
    data.linked_product_item_ids = this.linkedProductItemIdsControl.value ?? [];

    this.processing = true;

    this.eventService.createEventTimelineItem(data).subscribe({
      next: (response) => {
        this.itemCreated.emit(response.data);
        this.cancel();
        this.processing = false;
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
      },
    });
  }

  cancel() {
    this.form.reset();
    this.toggleOpen.emit();
  }

  productItemCreated($event: EventProductItemModel) {
    this.productItems.push($event);
  }

  removeProductItem(productItem: EventProductItemModel) {
    this.productItems = this.productItems.filter((item) => item !== productItem);
  }

  getProductDisplayPrice(productItem: EventProductItemModel): number {
    if (!productItem) {
      return 0;
    }
    const calculated = productItem.calculated_price ?? null;
    if (calculated !== null && calculated !== undefined) {
      return calculated;
    }
    if (productItem.amount !== null && productItem.amount !== undefined) {
      return productItem.amount;
    }
    return 0;
  }

  private loadExistingProducts(): void {
    this.eventProductItemService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 500,
          filter: [{ field: 'event_id', value: this.event.id }],
        }),
      )
      .subscribe({
        next: (res) => {
          this.existingProductChoices = (res.data ?? []).map((item) => ({
            value: item.id,
            label: this.formatExistingProductLabel(item),
          }));
        },
      });
  }

  private trackLinkedProductSelectionChanges(): void {
    this.linkedProductItemIdsControl.valueChanges.subscribe(() => {
      this.form.markAsDirty();
    });
  }

  private formatExistingProductLabel(item: EventProductItemModel): string {
    const qty = item.quantity ?? 0;
    const linkedName = item.event_timeline_item?.name;
    const linkSuffix = linkedName ? ` (Linked: ${linkedName})` : ' (Unscheduled)';

    return `${item.product?.name || 'Product'} x${qty}${linkSuffix}`;
  }
}
